/**
 * Buyer-chat message formatting: paragraphs, listing-fact bullets, area boxes.
 * CommonJS so backend tests can require this file.
 */

function cleanText(text) {
  return String(text || '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/#{1,6}\s/g, '')
    .trim();
}

function looksLikeCivicAddress(line) {
  const t = String(line || '').trim();
  if (!t || t.length > 80) return false;
  if (/^(it'?s|this|that|here|the home|the house|the property|i have|there'?s)\b/i.test(t)) {
    return false;
  }
  if (/^\d{1,6}\s+\S/.test(t)) return true;
  return /\b(street|st\.?|road|rd\.?|drive|dr\.?|avenue|ave\.?|lane|ln\.?|crescent|cres\.?|place|pl\.?|boulevard|blvd\.?|highway|hwy\.?|court|ct\.?|way|terrace|circle|cir\.?|trail)\b/i.test(t);
}

function isPropertyListing(lines) {
  if (lines.length < 2) return false;
  if (!looksLikeCivicAddress(lines[0])) return false;
  const second = lines[1];
  return (
    /\$[\d,]+/.test(second) ||
    /\d+\s*(bed|bedroom)/i.test(second) ||
    /\d+\s*(bath|bathroom)/i.test(second)
  );
}

const AREA_SENTENCE_START_RE = /^(there'?s|there is|there are|it'?s|this|that|here|the|a|an|i|we|you|and|but|so|also|plus|with)\b/i;

function parseAreaSection(lines) {
  if (!Array.isArray(lines) || !lines.length) return null;
  const first = lines[0];
  // Colon or spaced dash only — never a hyphen inside "single-car".
  const m = String(first || '').match(/^([A-Za-z.'’\s]{2,40})\s*(?::|[–—])\s+(.*)$/);
  if (!m) return null;
  const area = String(m[1] || '').trim();
  const firstDesc = String(m[2] || '').trim();
  if (!area || !firstDesc) return null;
  if (AREA_SENTENCE_START_RE.test(area)) return null;
  if (!/^[A-Z]/.test(area)) return null;
  // A place name is title case ("Logy Bay", "Bay of Islands"); "What draws
  // you more — ..." is a question, not a heading.
  if (!area.split(/\s+/).every((w) => /^[A-Z]/.test(w) || /^(of|the|and|de|la)$/.test(w))) {
    return null;
  }
  const descriptionLines = [firstDesc, ...lines.slice(1)].filter(Boolean);
  if (!descriptionLines.length) return null;
  return { area, description: descriptionLines.join(' ') };
}

// "St. John's" and "Mt. Pearl" are not sentence ends (Lillian's test, 2026-10-25:
// a question was cut into bullets at "St.").
const ABBR_BEFORE_RE = /\b(?:St|Ste|Mt|Dr|Rd|Ave|Mr|Mrs|Ms|e\.g|i\.e)\.$/i;

function splitSentences(text) {
  const parts = String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
    .split(/(?<=[.!?])\s+/);
  const out = [];
  for (const part of parts) {
    if (out.length && ABBR_BEFORE_RE.test(out[out.length - 1])) out[out.length - 1] += ` ${part}`;
    else out.push(part);
  }
  return out.map((s) => s.trim()).filter(Boolean);
}

function isListingFactSentence(s) {
  const t = String(s || '').trim();
  if (!t || t.length > 220) return false;
  if (/\?/.test(t)) return false;
  if (/^(want me|would you|shall i|any other|i can also|none of|i'?m showing|here are|want more|i have a few|the rest)\b/i.test(t)) {
    return false;
  }
  return true;
}

function looksLikeAddressHeading(line) {
  const t = String(line || '').trim();
  if (!t) return false;
  // A home heading starts with its house number. "Great, downtown St. John's
  // it is!" is a sentence, not an address (Lillian's test, 2026-10-25).
  if (!/^\d{1,6}[A-Za-z]?\s+\S/.test(t)) return false;
  if (looksLikeCivicAddress(t.split(/[—–,-]/)[0])) return true;
  return looksLikeCivicAddress(t);
}

function promoteListingFacts(blocks) {
  if (!Array.isArray(blocks) || !blocks.length) return blocks;
  const firstText = blocks.find((b) => b.type === 'text');
  if (!firstText || !looksLikeAddressHeading(firstText.lines[0])) return blocks;

  const out = [];
  let sawHeading = false;

  const flushFacts = (facts) => {
    for (const text of facts) out.push({ type: 'fact', text });
  };

  for (const block of blocks) {
    if (block.type === 'item' && sawHeading && /^[-•]/.test(block.line || '')) {
      out.push({ type: 'fact', text: String(block.line).replace(/^[-•]\s*/, '') });
      continue;
    }
    if (block.type !== 'text') {
      out.push(block);
      continue;
    }
    const lines = [...block.lines];
    if (!sawHeading && looksLikeAddressHeading(lines[0])) {
      sawHeading = true;
      out.push({ type: 'text', lines: [lines[0]] });
      lines.shift();
    }
    const facts = [];
    const leftover = [];
    for (const line of lines) {
      const sentences = splitSentences(line);
      if (sentences.length > 1 || (sawHeading && sentences.length === 1 && isListingFactSentence(sentences[0]))) {
        for (const sent of sentences) {
          if (sawHeading && isListingFactSentence(sent)) facts.push(sent);
          else leftover.push(sent);
        }
      } else {
        leftover.push(line);
      }
    }
    flushFacts(facts);
    if (leftover.length) out.push({ type: 'text', lines: leftover });
  }
  return out;
}

// Ava must credit owner lines ("the seller told Sarah", "the owner mentioned"),
// so the credit marks where one starts.
const OWNER_NOTE_RE = /\b(?:the|their) (?:sellers?|owners?)\b[^.?!]{0,30}?\b(?:told|tells|mentioned|mentions|said|says|shared|shares|noted|notes|wrote|describes?|calls?)\b|\baccording to the (?:sellers?|owners?)\b/i;
const CLAUSE_BREAK_RE = /\s+[—–]\s+|;\s+|,\s+and\s+|,\s+(?=(?:the|their) (?:sellers?|owners?)\b)/g;

function capitalize(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** A sentence split into [before, owner note]: the note starts at the clause that credits the owner. */
function splitOwnerSentence(sentence) {
  const m = OWNER_NOTE_RE.exec(sentence);
  if (!m) return null;
  let cut = 0;
  let breakEnd = 0;
  for (const b of sentence.slice(0, m.index).matchAll(CLAUSE_BREAK_RE)) {
    cut = b.index;
    breakEnd = b.index + b[0].length;
  }
  if (!cut) return ['', sentence];
  const before = sentence.slice(0, cut).replace(/[,;:\s]+$/, '');
  return [`${before}.`, capitalize(sentence.slice(breakEnd))];
}

/**
 * The owner's own words get their own small box, so they stand out from the
 * listing facts (Lillian, 2026-10-26).
 */
function markOwnerNotes(blocks) {
  const out = [];
  for (const block of blocks) {
    if (block.type === 'fact' || block.type === 'item') {
      const text = block.type === 'fact' ? block.text : String(block.line || '').replace(/^[-•]\s*/, '');
      const isNumbered = block.type === 'item' && /^\d/.test(block.line || '');
      if (!isNumbered && OWNER_NOTE_RE.test(text)) {
        const [before, note] = splitOwnerSentence(text);
        if (before) out.push(block.type === 'fact' ? { type: 'fact', text: before } : { type: 'item', line: `- ${before}` });
        out.push({ type: 'owner', text: note });
        continue;
      }
      out.push(block);
      continue;
    }
    if (block.type !== 'text') {
      out.push(block);
      continue;
    }
    let lines = [];
    const flush = () => {
      if (lines.length) out.push({ type: 'text', lines });
      lines = [];
    };
    for (const line of block.lines) {
      if (!OWNER_NOTE_RE.test(line)) {
        lines.push(line);
        continue;
      }
      let prose = [];
      for (const sent of splitSentences(line)) {
        const parts = splitOwnerSentence(sent);
        if (!parts) {
          prose.push(sent);
          continue;
        }
        if (parts[0]) prose.push(parts[0]);
        if (prose.length) lines.push(prose.join(' '));
        prose = [];
        flush();
        out.push({ type: 'owner', text: parts[1] });
      }
      if (prose.length) lines.push(prose.join(' '));
    }
    flush();
  }
  return out;
}

function formatMessage(text) {
  const clean = cleanText(text);
  const areaLabels = [
    "St. John's",
    'Mount Pearl',
    'Paradise',
    'Conception Bay South',
    'Portugal Cove-St. Philip\'s',
    'Torbay',
    'CBS',
    'Corner Brook',
    'Gander',
    'Clarenville',
    'Bay Roberts',
    'Carbonear',
    'Grand Falls-Windsor',
    'Marystown',
    'Stephenville',
  ];
  const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const areaMarker = new RegExp(`\\b(${areaLabels.map(escapeRegExp).join('|')})\\s*[:–—]`, 'gi');
  const normalizedForAreas = clean.replace(areaMarker, (match, city, offset, full) => {
    if (!offset) return `${city}:`;
    const before = full.slice(0, offset);
    // Only a town that opens a line or a sentence is a heading. "The suburbs
    // — Mount Pearl, Paradise, and Conception Bay South — offer more space"
    // must stay one sentence.
    if (!/(\n|[.!?]\s+)$/.test(before)) return match;
    return `${/\n\n$/.test(before) ? '' : '\n\n'}${city}:`;
  });
  const paragraphs = normalizedForAreas.split(/\n\n+/);
  const blocks = [];

  for (const para of paragraphs) {
    const lines = para.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) continue;

    if (isPropertyListing(lines)) {
      blocks.push({ type: 'property', lines });
      continue;
    }

    const areaSection = parseAreaSection(lines);
    if (areaSection) {
      blocks.push({ type: 'area_section', ...areaSection });
      continue;
    }

    let textAccum = [];
    for (const line of lines) {
      const isNumbered = /^\d+[.\-)]/.test(line);
      const isDash = /^[-•]/.test(line);
      if (isNumbered || isDash) {
        if (textAccum.length > 0) {
          blocks.push({ type: 'text', lines: [...textAccum] });
          textAccum = [];
        }
        blocks.push({ type: 'item', line });
      } else {
        textAccum.push(line);
      }
    }
    if (textAccum.length > 0) {
      blocks.push({ type: 'text', lines: textAccum });
    }
  }

  return markOwnerNotes(promoteListingFacts(blocks));
}

module.exports = {
  cleanText,
  looksLikeCivicAddress,
  parseAreaSection,
  formatMessage,
  promoteListingFacts,
  markOwnerNotes,
};
